// The catalogue contains spending rules, not market assumptions or financial advice.
// Initial spending remains a personal input. Rules are fixed in this first release.
export const SWR_LEGACY_WITHDRAWAL_STRATEGY_VERSION = 'annual-withdrawal-strategies-v1';
export const SWR_WITHDRAWAL_STRATEGY_VERSION = 'annual-withdrawal-strategies-v2';
export const SWR_WITHDRAWAL_CUSTOMIZATION_VERSION = 'real-monthly-floor-v1';

// Optional user overlay, independent of the selected rule and its annual state.
// The amount is net monthly income in euros of valuationDate, including when
// the plan starts in the future. It is never scaled with a card's candidate.
export function normalizeSWRWithdrawalCustomization(config) {
  if (config == null) return null;
  if (typeof config !== 'object' || Array.isArray(config)
    || config.version !== SWR_WITHDRAWAL_CUSTOMIZATION_VERSION
    || Object.keys(config).some(key => !['version', 'minimumMonthlyRealIncome'].includes(key))
    || typeof config.minimumMonthlyRealIncome !== 'number'
    || !Number.isFinite(config.minimumMonthlyRealIncome)
    || config.minimumMonthlyRealIncome < 0 || config.minimumMonthlyRealIncome > 1e15) {
    throw new Error('Personalizzazione della strategia non valida: verifica versione e reddito minimo mensile.');
  }
  return { version: SWR_WITHDRAWAL_CUSTOMIZATION_VERSION, minimumMonthlyRealIncome: config.minimumMonthlyRealIncome };
}

// Preserve descriptions associated with saved v1 results, as well as their inputs.
const LEGACY_STRATEGIES = Object.freeze([
  {
    id: 'personalized', label: 'Strategia Personalizzata',
    description: 'Definisci il piano dei prelievi, i guardrail e la riserva di liquidità.',
    fixedRules: 'Mantiene le opzioni della configurazione personalizzata.',
  },
  {
    id: 'vanguard', label: 'Vanguard',
    description: 'Una percentuale del patrimonio con limiti alla variazione annuale della spesa.',
    fixedRules: 'Spesa annuale tra −2,5% e +5% rispetto alla spesa precedente rivalutata per l’inflazione. Nessun buffer tattico.',
  },
  {
    id: 'guyton-klinger', label: 'Guyton–Klinger',
    description: 'Aumenta o riduce la spesa quando il tasso di prelievo supera le soglie della strategia.',
    fixedRules: 'Soglie 120% e 80%, variazioni del 10%, congelamento condizionale dell’inflazione e tagli sospesi negli ultimi 15 anni. Nessun pavimento aggiuntivo o buffer tattico.',
  },
  {
    id: 'constant-real', label: 'Importo reale costante',
    description: 'Rivaluta il prelievo iniziale una volta all’anno secondo l’inflazione.',
    fixedRules: 'Importo annuale netto diviso in dodici pagamenti mensili. Nessun guardrail o buffer tattico.',
  },
  {
    id: 'skip-inflation', label: 'Salta l’inflazione dopo una perdita',
    description: 'Dopo un rendimento annuo negativo mantiene il prelievo nominale dell’anno precedente.',
    fixedRules: 'L’adeguamento saltato non viene recuperato. Decisione annuale, nessun guardrail o buffer tattico.',
  },
].map(strategy => Object.freeze({ ...strategy, version: SWR_LEGACY_WITHDRAWAL_STRATEGY_VERSION, parameters: Object.freeze([]) })));

const CURRENT_RULES = {
  vanguard: 'Spesa annuale tra −2,5% e +5% rispetto alla spesa precedente rivalutata per l’inflazione. Guardrail fissi; buffer facoltativo separato.',
  'guyton-klinger': 'Soglie 120% e 80% del tasso iniziale; variazioni della spesa del 10%. Inflazione saltata dopo perdite se il tasso supera quello iniziale; tagli sospesi negli ultimi 15 anni. Guardrail fissi; buffer facoltativo separato.',
  'constant-real': 'Rivalutazione annuale del prelievo iniziale. Guardrail percentuali e buffer facoltativi personalizzano la regola di base.',
  'skip-inflation': 'L’adeguamento saltato non viene recuperato. Guardrail percentuali e buffer facoltativi personalizzano la regola di base.',
};
export const SWR_WITHDRAWAL_STRATEGIES = Object.freeze(LEGACY_STRATEGIES.map(strategy => Object.freeze({
  ...strategy, version: SWR_WITHDRAWAL_STRATEGY_VERSION,
  fixedRules: CURRENT_RULES[strategy.id] || strategy.fixedRules,
})));

export function getSWRWithdrawalStrategyDefinition(config) {
  const strategy = normalizeSWRWithdrawalStrategy(config);
  return (strategy.version === SWR_LEGACY_WITHDRAWAL_STRATEGY_VERSION ? LEGACY_STRATEGIES : SWR_WITHDRAWAL_STRATEGIES)
    .find(item => item.id === strategy.id);
}

// UI availability describes explicit future choices; v1 inputs themselves still
// exclude both overlays until a user chooses one and adopts v2.
export function getSWRWithdrawalStrategyCapabilities(config) {
  const strategy = normalizeSWRWithdrawalStrategy(config);
  const personalized = strategy.id === 'personalized';
  const builtInGuardrails = ['vanguard', 'guyton-klinger'].includes(strategy.id);
  const current = strategy.version === SWR_WITHDRAWAL_STRATEGY_VERSION;
  return { builtInGuardrails, editableGuardrails: !builtInGuardrails,
    allowsGuardrails: personalized || (current && !builtInGuardrails),
    allowsCashBuffer: personalized || current };
}

export function normalizeSWRWithdrawalStrategy(config) {
  if (config == null) return { id: 'personalized', version: SWR_WITHDRAWAL_STRATEGY_VERSION, parameters: {} };
  if (typeof config !== 'object' || Array.isArray(config)) throw new Error('Strategia di decumulo non valida.');
  const strategy = SWR_WITHDRAWAL_STRATEGIES.find(item => item.id === config.id);
  if (!strategy) throw new Error('Strategia di decumulo non riconosciuta.');
  if (![SWR_WITHDRAWAL_STRATEGY_VERSION, SWR_LEGACY_WITHDRAWAL_STRATEGY_VERSION].includes(config.version)) throw new Error('Versione della strategia di decumulo non supportata.');
  const parameters = config.parameters ?? {};
  if (typeof parameters !== 'object' || Array.isArray(parameters) || Object.keys(parameters).length) {
    throw new Error('La strategia selezionata non ammette parametri aggiuntivi.');
  }
  return { id: strategy.id, version: config.version, parameters: {} };
}

/**
 * A pure annual spending decision. All amounts are nominal net income.
 * Wealth includes cash and is net of explicit debt. The market-return signal is
 * supplied by the accounting loop; neither balances after withdrawals nor future
 * returns may be substituted for it. State never resets at a need-period boundary.
 */
export function getSWRAnnualWithdrawalDecision({ strategyId, state, year, totalYears,
  initialAnnualNominal, wealth, inflationIndex, previousYearReturn = 0 }) {
  if (strategyId === 'personalized' || !SWR_WITHDRAWAL_STRATEGIES.some(item => item.id === strategyId)) {
    throw new Error('La strategia selezionata non usa la decisione annuale.');
  }
  const currentWealth = Math.max(0, wealth);
  const initial = Math.max(0, initialAnnualNominal);
  if (!state) {
    const annualExpenseNominal = initial;
    return { annualExpenseNominal, monthlyExpenseNominal: annualExpenseNominal / 12,
      wasCut: false, wasRaised: false, inflationSkipped: false,
      state: { initialAnnualNominal: initial, initialInflationIndex: inflationIndex,
        initialWithdrawalRate: currentWealth > 0 ? initial / currentWealth : 0,
        lastAnnualNominal: initial, lastInflationIndex: inflationIndex } };
  }
  const inflationRatio = inflationIndex / state.lastInflationIndex;
  const adjustedPrevious = state.lastAnnualNominal * inflationRatio;
  let annualExpenseNominal = adjustedPrevious;
  let wasCut = false, wasRaised = false, inflationSkipped = false;
  if (strategyId === 'constant-real') {
    annualExpenseNominal = state.initialAnnualNominal * inflationIndex / state.initialInflationIndex;
  } else if (strategyId === 'skip-inflation') {
    inflationSkipped = previousYearReturn < 0;
    annualExpenseNominal = inflationSkipped ? state.lastAnnualNominal : adjustedPrevious;
  } else if (strategyId === 'vanguard') {
    const target = state.initialWithdrawalRate * currentWealth;
    annualExpenseNominal = Math.min(adjustedPrevious * 1.05, Math.max(adjustedPrevious * 0.975, target));
    wasCut = annualExpenseNominal < adjustedPrevious;
    wasRaised = annualExpenseNominal > adjustedPrevious;
  } else {
    const rate = amount => amount === 0 ? 0 : currentWealth > 0 ? amount / currentWealth : Infinity;
    inflationSkipped = previousYearReturn < 0 && rate(adjustedPrevious) > state.initialWithdrawalRate;
    if (inflationSkipped) annualExpenseNominal = state.lastAnnualNominal;
    if (totalYears - year + 1 > 15 && rate(annualExpenseNominal) > state.initialWithdrawalRate * 1.2) {
      annualExpenseNominal *= 0.9;
      wasCut = true;
    } else if (rate(annualExpenseNominal) < state.initialWithdrawalRate * 0.8) {
      annualExpenseNominal *= 1.1;
      wasRaised = true;
    }
  }
  return { annualExpenseNominal, monthlyExpenseNominal: annualExpenseNominal / 12,
    wasCut, wasRaised, inflationSkipped,
    state: { ...state, lastAnnualNominal: annualExpenseNominal, lastInflationIndex: inflationIndex } };
}
